import { FC, createContext, useContext, useState, useEffect, ReactNode } from "react";

/**
 * Payment Mode Context
 * Handles TEST vs PROD payment modes
 */

interface PaymentModeContextType {
  isTestMode: boolean;
  isLoading: boolean;
}

const PaymentModeContext = createContext<PaymentModeContextType>({
  isTestMode: false,
  isLoading: true,
});

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";

export const PaymentModeProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [isTestMode, setIsTestMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Fetch payment mode from health endpoint
    const checkPaymentMode = async () => {
      try {
        const response = await fetch(`${API_BASE_URL.replace("/api", "")}/health`);
        const data = await response.json();
        setIsTestMode(data.paymentMode === "TEST");
      } catch (error) {
        console.error("Failed to check payment mode:", error);
        setIsTestMode(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkPaymentMode();
  }, []);

  return (
    <PaymentModeContext.Provider value={{ isTestMode, isLoading }}>
      {children}
    </PaymentModeContext.Provider>
  );
};

export const usePaymentMode = () => useContext(PaymentModeContext);

/**
 * Test Mode Banner Component
 * Displays warning when payment mode is TEST
 */
export const TestModeBanner: FC = () => {
  const { isTestMode, isLoading } = usePaymentMode();

  if (isLoading || !isTestMode) {
    return null;
  }

  return (
    <div className="bg-yellow-500 text-yellow-900 px-4 py-2 text-center font-semibold text-sm">
      ⚠️ TEST MODE - All payments are ₹1. Do not use in production!
    </div>
  );
};
